"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { clearProfileWave, setProfileWave } from "@/services/api/profile-wave-api";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
type ProfileWaveAction = {
  readonly type: "set";
  readonly waveId: string;
} | {
  readonly type: "clear";
};
const getProfileIdentityKey = (profile: ApiIdentity | null): string | null => profile?.query ?? profile?.handle ?? profile?.primary_wallet ?? profile?.id ?? null;
export function useProfileWaveMutation(profile) {
  const $ = _c(26);
  const {
    requestAuth,
    setToast
  } = useAuth();
  const {
    onProfileEdit
  } = useContext(ReactQueryWrapperContext);
  let t0;
  if ($[0] !== profile) {
    t0 = async action => {
      const identity = getProfileIdentityKey(profile);
      if (!identity) {
        throw new Error("Unable to determine the profile identity.");
      }
      if (action.type === "set") {
        return await setProfileWave({
          identity,
          waveId: action.waveId
        });
      }
      return await clearProfileWave({
        identity
      });
    };
    $[0] = profile;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== onProfileEdit || $[3] !== profile || $[4] !== setToast) {
    t1 = (updatedProfile, action_0) => {
      onProfileEdit({
        profile: updatedProfile,
        previousProfile: profile
      });
      setToast({
        message: action_0.type === "set" ? "Profile wave updated." : "Profile wave cleared.",
        type: "success"
      });
    };
    $[2] = onProfileEdit;
    $[3] = profile;
    $[4] = setToast;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== setToast) {
    t2 = (error, action_1) => {
      const fallbackMessage = action_1.type === "set" ? "Unable to update profile wave." : "Unable to clear profile wave.";
      setToast({
        message: error instanceof Error ? error.message : fallbackMessage,
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const mutation = useMutation(t3);
  let t4;
  if ($[12] !== requestAuth) {
    t4 = async () => {
      const {
        success
      } = await requestAuth();
      return success;
    };
    $[12] = requestAuth;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const ensureAuthenticated = t4;
  let t5;
  if ($[14] !== ensureAuthenticated || $[15] !== mutation) {
    t5 = async action_2 => {
      if (!(await ensureAuthenticated())) {
        return null;
      }
      try {
        return await mutation.mutateAsync(action_2);
      } catch {
        return null;
      }
    };
    $[14] = ensureAuthenticated;
    $[15] = mutation;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const runProfileWaveMutation = t5;
  let t6;
  if ($[17] !== runProfileWaveMutation) {
    t6 = async waveId => await runProfileWaveMutation({
      type: "set",
      waveId
    });
    $[17] = runProfileWaveMutation;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const updateProfileWave = t6;
  let t7;
  if ($[19] !== runProfileWaveMutation) {
    t7 = async () => await runProfileWaveMutation({
      type: "clear"
    });
    $[19] = runProfileWaveMutation;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const clearSelectedProfileWave = t7;
  const t8 = mutation.variables?.type ?? null;
  let t9;
  if ($[21] !== clearSelectedProfileWave || $[22] !== mutation.isPending || $[23] !== t8 || $[24] !== updateProfileWave) {
    t9 = {
      updateProfileWave,
      clearSelectedProfileWave,
      isPending: mutation.isPending,
      pendingAction: t8
    };
    $[21] = clearSelectedProfileWave;
    $[22] = mutation.isPending;
    $[23] = t8;
    $[24] = updateProfileWave;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}